import { useState, useEffect } from 'react';
import { Star, Book, Heart, CloudSun, Smile, ArrowRight, User, LogOut, Plus, Trash2, Cat, Coins } from 'lucide-react';
import { UserProfile } from '../hooks/useProfile';
import { useFootprint } from '../hooks/useFootprint';
import { quranData, dailyPlan } from '../data/quran';

interface KidsDashboardProps {
  onOpenSession: (session: 'reading' | 'profile' | 'arabic') => void;
  profile: UserProfile;
  onExitKidsMode: () => void;
  day: number;
}

interface KidDeed {
  id: string;
  text: string;
  timestamp: number;
}

export function KidsDashboard({ onOpenSession, profile, onExitKidsMode, day }: KidsDashboardProps) {
  const { deeds, logDeed, hablCoins } = useFootprint();
  const [clicked, setClicked] = useState<string | null>(null);
  
  // Resolve Current Quest
  const surahId = dailyPlan[(day - 1) % dailyPlan.length];
  const currentSurah = quranData[surahId] || quranData[1];

  const [deedInput, setDeedInput] = useState('');
  const [kidDeeds, setKidDeeds] = useState<KidDeed[]>([]);
  const [showConfetti, setShowConfetti] = useState(false);

  // Load deeds from local storage on mount
  useEffect(() => {
    const saved = localStorage.getItem('habl_kids_deeds');
    if (saved) {
      try {
        setKidDeeds(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to load kids deeds", e);
      }
    }
  }, []);

  const saveDeeds = (deeds: KidDeed[]) => {
    setKidDeeds(deeds);
    localStorage.setItem('habl_kids_deeds', JSON.stringify(deeds));
  };

  const handleAddDeed = () => {
    if (!deedInput.trim()) return;

    const newDeed: KidDeed = {
      id: Date.now().toString(),
      text: deedInput.trim(),
      timestamp: Date.now(),
    };

    const updated = [newDeed, ...kidDeeds];
    saveDeeds(updated);
    
    // Log deed and Mint Coin
    logDeed(deedInput.trim()); 
    
    setDeedInput('');
    handleAction('deed');
    
    // Trigger celebration
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 3000);
  };

  const handleDeleteDeed = (id: string) => {
    const updated = kidDeeds.filter(d => d.id !== id);
    saveDeeds(updated);
  };

  // Simple "Good Deed Jar" visualization
  const totalDeeds = Object.values(deeds).reduce((a, b) => a + b, 0);
  const jarLevel = Math.min(totalDeeds * 2, 100); // Caps at 50 deeds for full jar visual

  const handleAction = (action: string, callback?: () => void) => {
    setClicked(action);
    setTimeout(() => {
      setClicked(null);
      if (callback) callback();
    }, 300);
  }; return (
    <div className="min-h-screen bg-sky-100 font-comic p-4 relative overflow-y-auto pb-20">
      {/* Background Decor */}
      <CloudSun className="absolute top-10 right-10 w-24 h-24 text-yellow-400 opacity-80 fixed" />
      <div className="absolute bottom-0 left-0 w-full h-32 bg-green-300 rounded-t-[50px] fixed -z-10"></div>
      
      {/* Confetti Overlay (Simple CSS Animation Placeholder) */}
      {showConfetti && (
        <div className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center">
            <div className="animate-bounce text-6xl">🎉</div>
        </div>
      )}

      {/* Header */}
      <div className="relative z-10 flex justify-between items-center mb-6 pt-4">
        <div>
          <h1 className="text-3xl font-black text-sky-900 tracking-tight">Habl Junior</h1>
          <p className="text-sky-700 font-bold">Salam, {profile.name}!</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={onExitKidsMode}
            className="w-12 h-12 bg-white/80 rounded-full flex items-center justify-center text-sky-600 hover:bg-white border-4 border-transparent hover:border-sky-200 transition-all shadow-sm"
            title="Exit Kids Mode"
          >
            <LogOut className="w-6 h-6" />
          </button>
          <button 
            onClick={() => onOpenSession('profile')}
            className="w-12 h-12 bg-white rounded-full border-4 border-yellow-300 shadow-lg flex items-center justify-center overflow-hidden"
          >
            {profile.avatar ? (
               <img src={profile.avatar} alt="Me" className="w-full h-full object-cover" />
             ) : (
               <User className="w-6 h-6 text-sky-400" />
             )}
          </button>
        </div>
      </div>

      {/* Cat & Coin Balance Card */}
      <div className="relative z-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-3xl p-6 shadow-xl border-b-8 border-indigo-700 mb-6 text-white overflow-hidden">
        <div className="flex items-center justify-between relative z-10">
            <div>
                <h2 className="text-indigo-100 font-bold text-sm uppercase tracking-wider mb-1">My Treasure</h2>
                <div className="flex items-center gap-2">
                    <Coins className="w-8 h-8 text-yellow-300" />
                    <span className="text-4xl font-black text-white">{hablCoins}</span>
                </div>
                <p className="text-xs text-indigo-200 mt-2">Habl Coins collected</p>
            </div>
            <div className="bg-white/10 p-4 rounded-full border-4 border-white/20">
                <Cat className="w-12 h-12 text-white" />
            </div>
        </div>
        
        {/* Cat Speech Bubble */}
        <div className="absolute -bottom-1 right-20 bg-white text-slate-800 px-3 py-1 rounded-t-xl rounded-bl-xl text-xs font-bold animate-bounce">
            Meow! Good job!
        </div>
      </div>

      {/* Main Quest Card */}
      <div 
        onClick={() => handleAction('quest', () => onOpenSession('reading'))}
        className={`relative z-10 bg-white rounded-3xl p-6 shadow-xl border-b-8 border-yellow-300 mb-6 transition-transform ${clicked === 'quest' ? 'scale-95' : 'hover:scale-105'} cursor-pointer`}
      >
        <div className="absolute -top-6 -left-4 bg-yellow-400 text-yellow-900 font-black px-4 py-2 rounded-xl rotate-[-10deg] shadow-sm">
          TODAY'S QUEST
        </div>
        
        <div className="flex items-center gap-4 mt-2">
          <div className="bg-orange-100 p-4 rounded-2xl">
            <Book className="w-10 h-10 text-orange-500" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">{currentSurah.englishName}</h2>
            <p className="text-slate-500 font-medium">Read Surah {currentSurah.name}</p>
          </div>
          <ArrowRight className="w-8 h-8 text-yellow-400 ml-auto" />
        </div>
      </div>

      {/* The Good Deed Jar & Input Section */}
      <div className="relative z-10 bg-white/80 backdrop-blur rounded-3xl p-6 border-b-8 border-green-300 mb-6 space-y-6">
         <div>
            <h3 className="font-bold text-green-800 flex items-center gap-2 mb-4">
            <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" /> My Good Deeds Jar
            </h3>
            
            <div className="relative w-full h-32 bg-slate-100 rounded-2xl border-4 border-slate-200 overflow-hidden flex items-end justify-center mb-6">
                {/* The "Liquid" */}
                <div 
                className="w-full bg-yellow-300 transition-all duration-1000 ease-out relative"
                style={{ height: `${jarLevel}%` }}
                >
                {/* Bubbles */}
                <div className="absolute top-2 left-4 w-2 h-2 bg-white rounded-full opacity-50 animate-bounce"></div>
                <div className="absolute top-6 right-8 w-3 h-3 bg-white rounded-full opacity-50 animate-bounce delay-75"></div>
                </div>
                
                {/* Counter */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-4xl font-black text-slate-800 drop-shadow-sm">
                {totalDeeds}
                </div>
            </div>
         </div>
         
         {/* Input Area */}
         <div className="bg-green-50 p-4 rounded-2xl border-2 border-green-100">
            <label className="block text-green-800 font-bold mb-2 text-sm">What good did you do today?</label>
            <div className="flex gap-2">
                <input 
                    type="text" 
                    value={deedInput}
                    onChange={(e) => setDeedInput(e.target.value)}
                    placeholder="I shared my toys..."
                    className="flex-1 px-4 py-3 rounded-xl border-2 border-green-200 focus:border-green-400 focus:outline-none font-medium text-slate-700 bg-white placeholder:text-slate-300"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddDeed()}
                />
                <button 
                    onClick={handleAddDeed}
                    disabled={!deedInput.trim()}
                    className="bg-green-500 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white p-3 rounded-xl font-bold shadow-md transition-all active:scale-95"
                >
                    <Plus className="w-6 h-6" />
                </button>
            </div>
         </div>

         {/* Recent Deeds List */}
         {kidDeeds.length > 0 && (
             <div className="space-y-3 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
                 <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">My Recent Deeds</h4>
                 {kidDeeds.map((deed) => (
                     <div key={deed.id} className="bg-white p-3 rounded-xl border border-slate-100 shadow-sm flex items-center justify-between group animate-in fade-in slide-in-from-bottom-2 duration-300">
                         <div className="flex items-center gap-3">
                             <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                                 <Smile className="w-5 h-5 text-yellow-600" />
                             </div>
                             <span className="font-medium text-slate-700 text-sm">{deed.text}</span>
                         </div>
                         <button 
                            onClick={() => handleDeleteDeed(deed.id)}
                            className="text-slate-300 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                        >
                             <Trash2 className="w-4 h-4" />
                         </button>
                     </div>
                 ))}
             </div>
         )}
      </div>

      {/* Mini Games Grid */}
      <div className="relative z-10 grid grid-cols-2 gap-4">
        <div 
          onClick={() => onOpenSession('arabic')}
          className="bg-sky-50 p-4 rounded-2xl border-b-4 border-sky-300 cursor-pointer active:border-b-0 active:translate-y-1 transition-all"
        >
           <span className="text-4xl block mb-1">ا ب ت</span>
           <h4 className="font-bold text-sky-800">Learn Arabic</h4>
        </div>
        <div className="bg-pink-100 p-4 rounded-2xl border-b-4 border-pink-300">
           <Heart className="w-8 h-8 text-pink-500 mb-2" />
           <h4 className="font-bold text-pink-800">Kindness</h4>
        </div>
      </div>

    </div>
  );
}
